import {
  AnalyticsEvent,
  ButtonComponentV2,
  SlTitledListComponent,
  Typenames,
} from '@varo/graphql-schema-types';
import { Href, useRouter } from 'expo-router';
import { mapToTransactionSectionList } from 'features/directDeposit/helpers/mapToTransactionSectionList';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { parseGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import TransactionsSectionList from 'shared/components/TransactionSectionList/TransactionSectionList';
import Button from 'shared/components/vxl/Button/Button';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import EmptyStateScreen from 'shared/patterns/vxl/EmptyStateScreen/EmptyStateScreen';
import theme from 'shared/styles/theme';

interface DirectDepositHistoryTransactionsProps {
  transactionsData: SlTitledListComponent[];
  footerButton?: ButtonComponentV2 | null;
}

const DirectDepositHistoryTransactions = ({
  transactionsData,
  footerButton,
}: DirectDepositHistoryTransactionsProps) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('directDeposit');

  const { segmentClient } = useAnalyticsConfiguration();

  const transactionsMap = transactionsData.map(mapToTransactionSectionList);

  const handleFooterPress = () => {
    const action = footerButton?.buttonOnClick;
    if (
      action?.__typename === Typenames.RouteNavigationAction &&
      action.route
    ) {
      const { name, properties } = parseGQLAnalyticsEvent(
        action.analyticsEvent as AnalyticsEvent,
      );
      void segmentClient.logEvent(name, properties);
      router.push(action.route as Href);
    }
  };

  const renderEmptyState = () => {
    return (
      <View style={styles.defaultSpacing}>
        <BodySmBoldText style={styles.emptyTitle}>
          {translatedText('emptyDepositHistory.title')}
        </BodySmBoldText>
        <EmptyStateScreen
          bodyText={translatedText('emptyDepositHistory.description')}
          headlineText=""
          illustrationKey="PAYCHECK_A"
          bodyTextStyle={styles.emptyBodyText}
        />
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.sectionDivider} />
      {transactionsMap.length === 0 ? (
        renderEmptyState()
      ) : (
        <>
          <TransactionsSectionList
            scrollEnabled={false}
            transactions={transactionsMap}
          />
          {footerButton?.buttonText ? (
            <View style={styles.defaultSpacing}>
              <Button
                label={footerButton.buttonText}
                type={footerButton.buttonStyle}
                onPress={handleFooterPress}
              />
            </View>
          ) : null}
        </>
      )}
    </View>
  );
};

export default DirectDepositHistoryTransactions;

const styles = StyleSheet.create({
  container: {
    marginBottom: theme.spacing.m,
  },
  defaultSpacing: {
    marginHorizontal: theme.spacing.m,
    marginTop: theme.spacing.m,
  },
  emptyBodyText: {
    color: theme.color.text.secondary,
  },
  emptyTitle: {
    marginTop: theme.spacing.m,
  },
  sectionDivider: {
    backgroundColor: theme.color.background.tertiary,
    height: theme.spacing.s,
    marginTop: theme.spacing.m,
  },
});
